import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { mdiInformationOutline, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import { AxiosResponse } from "axios";

import Button from "components/Button";
import Modal from "components/Modal";
import Toggle from "components/Toggle";
import Tooltip from "components/Tooltip";
import DefaultPaymentSpeedSettingItem from "features/main/HaulPay/modules/Broker/DefaultPaymentSpeedSettingItem";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IPaymentSpeed } from "models/HaulPay";
import { getDefaultPaymentTermsAPI, updateDefaultPaymentTermAPI } from "services/api/haulpay";
import { AppDispatch } from "store";
import { updateFactoringProfile } from "store/user/actions";
import { selectFactoringProfile, selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";

type DefaultSettingsModalProps = {
  isOpen: boolean;
  closeModal: VoidFunction;
};

export default function DefaultSettingsModal({
  isOpen,
  closeModal
}: Readonly<DefaultSettingsModalProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const userDetails = useSelector(selectUserDetails);
  const factoringProfile = useSelector(selectFactoringProfile);
  const [defaultPaymentSettings, setDefaultPaymentSettings] = useState<IPaymentSpeed[]>([]);
  const [factoringPaymentTerm, setFactoringPaymentTerm] = useState<IPaymentSpeed[]>([]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isSubmitting }
  } = useForm();

  const getDefaultPaymentSettings = async (factoring_id: string) => {
    const paymentSettingsResponse: AxiosResponse<IPaymentSpeed[]> =
      await getDefaultPaymentTermsAPI(factoring_id);
    setDefaultPaymentSettings(
      paymentSettingsResponse.data.filter((item) => item.payee === "carrier")
    );
    const factoringCompanyTerms = paymentSettingsResponse.data.filter(
      (setting) => setting.payee === "factoring_company"
    );
    setFactoringPaymentTerm(factoringCompanyTerms);
    reset({
      terms: paymentSettingsResponse.data
        .filter((item) => item.payee === "carrier")
        .map((item) => ({
          ...item,
          discount_rate: item.discount_rate + "%",
          carrier_rate: item.carrier_rate + "%"
        })),
      factoringCompanyTerms: factoringCompanyTerms.map((item) => ({
        ...item,
        discount_rate: item.discount_rate + "%",
        carrier_rate: item.carrier_rate + "%"
      }))
    });
  };

  const onToggleRateConfirmationEmailUpdate = async (checked: boolean) => {
    dispatch(
      updateFactoringProfile({
        factoring_id: userDetails.factoring_id,
        params: {
          send_rate_confirmation_pdf: checked
        }
      })
    );
  };

  const onSubmit = async (data: {
    terms: IPaymentSpeed[];
    factoringCompanyTerms: IPaymentSpeed[];
  }) => {
    const updateSettingPromises: Promise<any>[] = [];
    data.terms.forEach((term) =>
      updateSettingPromises.push(
        updateDefaultPaymentTermAPI(userDetails.factoring_id, term.id, {
          ...term,
          carrier_rate: parseFloat(term.carrier_rate + ""),
          discount_rate: parseFloat(term.discount_rate + "")
        })
      )
    );
    data.factoringCompanyTerms.forEach((term) =>
      updateSettingPromises.push(
        updateDefaultPaymentTermAPI(userDetails.factoring_id, term.id, {
          ...term,
          carrier_rate: parseFloat(term.carrier_rate + ""),
          discount_rate: parseFloat(term.discount_rate + "")
        })
      )
    );
    try {
      await Promise.all(updateSettingPromises);
      closeModal();
      showSuccessMessage("Payment terms has been updated.");
    } catch (e) {
      showErrorMessage("Error updating payment terms.");
    }
  };

  useEffect(() => {
    if (userDetails) {
      getDefaultPaymentSettings(userDetails.factoring_id);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      wrapperClass="!my-auto text-sm"
      dialogClass="!bg-modalBg !px-5 !py-5 !border !border-card !overflow-y-auto gap-4 desktop:gap-4.5 desktop:!px-12 desktop:!py-14"
      closeClass="!top-24 desktop:!top-28 !right-6 desktop:!right-12 !bg-transparent !text-placeholder">
      <h3 className="text-lg font-medium desktop:text-2xl text-primary">Edit Default Settings</h3>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3.5 text-text font-light">
          <span className="text-sm desktop:text-lg">
            These are the presets default options (if enabled) that invited carriers will get to
            select from during their boarding process to your HaulPay group.
          </span>

          <ul className="grid grid-cols-5 text-xs text-left desktop:text-base">
            <li>Enable Option</li>
            <li>Payment Speed</li>
            <li>HP Discount</li>
            <li>Your Portion %</li>
            <li>Carrier’s Rate</li>
          </ul>

          <div className="flex flex-col gap-4 leading-10 text-xxs desktop:text-sm whitespace-nowrap">
            {defaultPaymentSettings.map((paymentSetting, index) => (
              <DefaultPaymentSpeedSettingItem
                key={paymentSetting.id}
                index={index}
                control={control}
                register={register}
                getValues={getValues}
                setValue={setValue}
                paymentTerms={[...defaultPaymentSettings, ...factoringPaymentTerm]}
              />
            ))}
          </div>

          <ul className="desktop:grid desktop:grid-cols-5 desktop:gap-2">
            <li></li>
            <li className="flex items-center justify-between gap-1 desktop:text-base desktop:col-span-4">
              <span className="text-xs desktop:text-base">
                Choose the default payment speed for carriers boarding with a factoring company
              </span>
              <Tooltip
                position="left"
                message="This shows and sets the default payment speed for invited carriers that board with a factoring company.">
                <Icon path={mdiInformationOutline} className="flex-none size-5 text-primary" />
              </Tooltip>
            </li>
          </ul>
          <div className="flex flex-col gap-4 leading-10 text-xxs desktop:text-sm whitespace-nowrap">
            {factoringPaymentTerm.map((paymentSetting, index) => (
              <DefaultPaymentSpeedSettingItem
                key={paymentSetting.id}
                name="factoringCompanyTerms"
                index={index}
                control={control}
                register={register}
                getValues={getValues}
                setValue={setValue}
                paymentTerms={[...defaultPaymentSettings, ...factoringPaymentTerm]}
                isFactoringPaymentTerm
              />
            ))}
          </div>
          <div className={styles.toggle}>
            <Toggle
              checked={factoringProfile?.send_rate_confirmation_pdf ?? false}
              onChange={(event) => onToggleRateConfirmationEmailUpdate(event.target.checked)}
            />
            <span>Generate Carrier rate confirmation from HaulPay, email to carrier.</span>
          </div>
        </div>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="mt-4 ml-auto mr-auto rounded-full w-28 h-9 desktop:h-11 desktop:mr-0">
          {isSubmitting ? <Icon path={mdiLoading} size={1.5} spin={1} /> : "Save"}
        </Button>
      </form>
    </Modal>
  );
}
